import React, { useState } from 'react';
import {
  getBookings, BookingListResult, getUserProfile, getScholarProfile,
} from '../services/adminService';
import {
  pageContainer, cardStyle, statsGrid, labelStyle, valueStyle,
  subTextStyle, thStyle, tdStyle, badgeStyle, tableSearch, smallSelect,
} from '../styles/theme';
import { useAdminPage, useDebounce, useToast } from '../utils/useAdminPage';
import { formatScholarId } from '../utils/idFormatter';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';
import BookingDetailsModal from '../components/BookingDetailsModal';
import RefundModal from '../components/RefundModal';
import ReimbursementModal from '../components/ReimbursementModal';
import ProfileModal from '../components/ProfileModal';
import { exportToCSV } from '../utils/csvExport';
import API from '../services/api';

const EMPTY: BookingListResult = {
  items: [], summary: { total: 0, confirmed: 0, pending: 0, ongoing: 0, cancelled: 0 },
};

const BOOKING_STATUSES = [
  'PENDING', 'ACCEPTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'DECLINED', 'DISPUTED',
];

const Bookings = () => {
  const [search, setSearch]           = useState('');
  const [status, setStatus]           = useState('');
  const [religion, setReligion]       = useState('');
  const [page, setPage]               = useState(1);
  const [updatingId, setUpdatingId]   = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [refundBooking, setRefundBooking] = useState<any | null>(null);
  const [reimburseBooking, setReimburseBooking] = useState<any | null>(null);
  const pageSize = 10;

  // Profile modal state
  const [profileOpen, setProfileOpen]       = useState(false);
  const [profileType, setProfileType]       = useState<'user' | 'scholar'>('user');
  const [profileData, setProfileData]       = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const handleOpenProfile = async (id: string, type: 'user' | 'scholar') => {
    if (!id) return;
    setProfileType(type);
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    try {
      const data = type === 'user' ? await getUserProfile(id) : await getScholarProfile(id);
      setProfileData(data);
    } catch (err) {
      console.error(`Failed to load ${type} profile`, err);
    } finally {
      setProfileLoading(false);
    }
  };

  const dSearch = useDebounce(search, 350);
  const { toast, showToast }      = useToast();

  const cacheKey = `${dSearch}|${status}|${religion}`;

  const { data, loading, revalidating, syncLabel, lastSyncedAt, refresh, mutate } =
    useAdminPage<BookingListResult>(EMPTY, {
      cacheNs: 'bookings:',
      cacheKeySuffix: cacheKey,
      fetcher: () => getBookings({ search: dSearch, status, religion }),
    });

  const items   = data.items || [];
  const summary = data.summary;

  // Pagination
  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  // Stats cards
  const stats = [
    { label: 'Total Bookings', val: summary.total.toLocaleString(), sub: 'All time', color: '#2563eb', icon: '' },
    { label: 'Confirmed',      val: summary.confirmed.toString(),   sub: `${summary.total ? ((summary.confirmed / summary.total) * 100).toFixed(1) : 0}% rate`, color: '#16a34a', icon: '' },
    { label: 'Pending',        val: summary.pending.toString(),     sub: 'Awaiting action', color: '#d97706', icon: '⏳' },
    { label: 'In Progress',    val: summary.ongoing.toString(),     sub: 'Currently active', color: '#9333ea', icon: '' },
    { label: 'Cancelled',      val: summary.cancelled.toString(),   sub: `${summary.total ? ((summary.cancelled / summary.total) * 100).toFixed(1) : 0}% rate`, color: '#dc2626', icon: '' },
  ];

  const statusColor = (s: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      ACCEPTED:    { bg: '#f0fdf4', color: '#15803d' },
      COMPLETED:   { bg: '#f0fdf4', color: '#15803d' },
      PENDING:     { bg: '#fffbeb', color: '#b45309' },
      IN_PROGRESS: { bg: '#eff6ff', color: '#1e40af' },
      CANCELLED:   { bg: '#fef2f2', color: '#b91c1c' },
      DECLINED:    { bg: '#fef2f2', color: '#b91c1c' },
      DISPUTED:    { bg: '#fff1f2', color: '#9f1239' },
    };
    return map[s] || { bg: '#f1f5f9', color: '#475569' };
  };

  const handleStatusChange = async (bookingId: string, newStatus: string) => {
    setUpdatingId(bookingId);
    try {
      await API.patch(`/admin/bookings/${bookingId}/status`, { status: newStatus });
      showToast(`Booking updated to ${newStatus}`);
      mutate();
    } catch {
      showToast('Failed to update booking status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExportCSV = () => {
    const exportData = items.map((b: any) => ({
      BookingID: b.id,
      Customer: b.customer?.user?.name || 'N/A',
      Scholar: b.scholar?.user?.name || 'N/A',
      Service: b.service?.name || 'N/A',
      Religion: b.service?.religion || 'N/A',
      Status: b.status,
      Price: b.price || 0,
      PaymentStatus: b.payment?.status || 'PENDING',
      ScheduledAt: b.scheduledAt ? new Date(b.scheduledAt).toLocaleString() : 'N/A',
      CreatedAt: new Date(b.createdAt).toLocaleString(),
    }));
    exportToCSV(exportData, 'Bookings_Report');
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader
        breadcrumb="Dashboard > Bookings"
        title="Booking Management"
        subtitle="Monitor and manage all service bookings across the platform."
        syncLabel={syncLabel} revalidating={revalidating} loading={loading}
        lastSyncedAt={lastSyncedAt} onRefresh={refresh} onExport={handleExportCSV}
      />

      {/* Stats Grid */}
      <div style={{ ...statsGrid, gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {stats.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={labelStyle}>{s.label}</span>
              <span>{s.icon}</span>
            </div>
            <div style={valueStyle}>{s.val}</div>
            <div style={subTextStyle}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Table Section */}
      <div style={cardStyle}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search by ID, user name, scholar…"
            style={{ ...tableSearch, flex: 1, minWidth: 220 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <select style={smallSelect} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="ACCEPTED">Confirmed</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="DISPUTED">Disputed</option>
            </select>
            <select style={smallSelect} value={religion} onChange={e => { setReligion(e.target.value); setPage(1); }}>
              <option value="">All Religions</option>
              <option value="HINDUISM">Hinduism</option>
              <option value="ISLAM">Islam</option>
              <option value="CHRISTIANITY">Christianity</option>
              <option value="SIKHISM">Sikhism</option>
              <option value="BUDDHISM">Buddhism</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '1px solid #f1f5f9' }}>
              <th style={thStyle}>BOOKING ID</th>
              <th style={thStyle}>USER</th>
              <th style={thStyle}>SCHOLAR</th>
              <th style={thStyle}>SERVICE</th>
              <th style={thStyle}>DATE & TIME</th>
              <th style={thStyle}>AMOUNT</th>
              <th style={thStyle}>PAYMENT</th>
              <th style={thStyle}>STATUS</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading bookings…</td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No bookings found.</td></tr>
            ) : paged.map((b: any) => {
              const sc = statusColor(b.status);
              const isUpdating = updatingId === b.id;
              return (
                <tr
                  key={b.id}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.12s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  onClick={() => setSelectedBooking(b)}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#334155', fontFamily: 'monospace' }}>#{b.id.slice(0, 8)}</div>
                  </td>
                  <td style={tdStyle}>
                    <div
                      onClick={(e) => {
                        const uId = b.customer?.user?.id || b.customer?.userId || b.customer?.id || b.customerId;
                        if (uId) {
                          e.stopPropagation();
                          handleOpenProfile(uId, 'user');
                        }
                      }}
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: b.customer?.user?.name ? '#2563eb' : '#0f172a',
                        cursor: b.customer?.user?.name ? 'pointer' : 'default',
                        textDecoration: b.customer?.user?.name ? 'underline' : 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                      title="View Customer Profile"
                    >
                      {b.customer?.user?.name || 'Unknown'} {b.customer?.user?.name ? '↗' : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.customer?.user?.phone || ''}</div>
                  </td>
                  <td style={tdStyle}>
                    <div
                      onClick={(e) => {
                        const sId = b.scholar?.id || b.scholarId || b.scholar?.userId || b.scholar?.user?.id;
                        if (sId) {
                          e.stopPropagation();
                          handleOpenProfile(sId, 'scholar');
                        }
                      }}
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: b.scholar?.user?.name ? '#2563eb' : '#0f172a',
                        cursor: b.scholar?.user?.name ? 'pointer' : 'default',
                        textDecoration: b.scholar?.user?.name ? 'underline' : 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                      title="View Scholar Profile"
                    >
                      {b.scholar?.user?.name || 'Unknown'} {b.scholar?.user?.name ? '↗' : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                      {formatScholarId(b.scholar?.id || b.scholarId || b.scholar?.userId || b.scholar?.user?.id)}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{b.service?.name || '-'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.durationMin ? `${b.durationMin} mins` : ''}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: 13 }}>{b.scheduledAt ? new Date(b.scheduledAt).toLocaleString() : '-'}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>₹{b.price || 0}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      ...badgeStyle,
                      background: b.payment?.status === 'SUCCESS' ? '#f0fdf4' : b.payment?.status === 'REFUNDED' ? '#faf5ff' : '#fffbeb',
                      color: b.payment?.status === 'SUCCESS' ? '#15803d' : b.payment?.status === 'REFUNDED' ? '#7c3aed' : '#b45309',
                    }}>
                      {b.payment?.status === 'SUCCESS' ? '✓ Paid' : b.payment?.status === 'REFUNDED' ? '↩ Refunded' : '⏳ Pending'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {/* Editable status dropdown */}
                    <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                      <select
                        value={b.status}
                        disabled={isUpdating}
                        onChange={e => handleStatusChange(b.id, e.target.value)}
                        style={{
                          padding: '5px 28px 5px 10px',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          background: sc.bg,
                          color: sc.color,
                          border: `1.5px solid ${sc.color}33`,
                          cursor: isUpdating ? 'wait' : 'pointer',
                          outline: 'none',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          opacity: isUpdating ? 0.6 : 1,
                          minWidth: 110,
                          transition: 'all 0.15s',
                        }}
                      >
                        {BOOKING_STATUSES.map(s => (
                          <option key={s} value={s}>{s.replace('_', ' ')}</option>
                        ))}
                      </select>
                      {/* Chevron icon */}
                      <span style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        pointerEvents: 'none', fontSize: 9, color: sc.color,
                      }}>▼</span>
                    </div>
                    {b.review && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4,
                        padding: '2px 8px', borderRadius: 12,
                        background: '#fef3c7', color: '#b45309',
                        fontSize: 11, fontWeight: 700,
                      }} title={b.review.comment ? `Review: "${b.review.comment}"` : 'Customer Review'}>
                        <span style={{ color: '#d97706' }}>★</span>
                        <span>{b.review.rating}.0</span>
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedBooking(b); }}
                      style={{
                        padding: '5px 12px', borderRadius: 8, border: 'none',
                        background: '#eff6ff', color: '#2563eb',
                        fontWeight: 700, fontSize: 11, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Paginator total={items.length} page={page} pageSize={pageSize} onPage={setPage} />
      </div>

      <BookingDetailsModal
        open={!!selectedBooking}
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onReimburseClick={(b) => setReimburseBooking(b)}
        onRefundClick={(b) => setRefundBooking(b)}
      />

      <RefundModal
        booking={refundBooking}
        onClose={() => setRefundBooking(null)}
        onSuccess={() => {
          setSelectedBooking(null);
          mutate();
        }}
      />

      <ReimbursementModal
        booking={reimburseBooking}
        onClose={() => setReimburseBooking(null)}
        onSuccess={() => {
          setSelectedBooking(null);
          mutate();
        }}
      />

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        data={profileData}
        loading={profileLoading}
        type={profileType}
      />
    </div>
  );
};

export default Bookings;